import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import { GameSettingsContext } from './BridgeEngineSettings';
import { renderSuit } from './Hand';
import './Hand.css';

interface CardProps {
    id: string;
    suit: string;
    rank: string;
    canDrag: boolean;
    makeGray: boolean;
    revealed: boolean;
    onCardDragEnd: (id: string, newOrigin: DOMPoint) => void;
}

const Card: React.FC<CardProps> = ({ id, suit, rank, canDrag, makeGray, revealed, onCardDragEnd}) => {
    const gameContextStateContext = useContext(GameSettingsContext)!;
    const { gameSettings } = gameContextStateContext;
    
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [location, setLocation] = useState<DOMPoint | null>(null);
    
    const cardRef = useRef<HTMLDivElement>(null);
    const cardLocationRef = useRef<DOMPoint | null>(null);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const cardDiv = cardRef.current;
        if (!cardDiv) return; //TODO: throw error? why is this handler being set for a non-existent card?
        if (!canDrag) return; //TODO: throw error? this handler should have not been set.

        const onMouseMove = (event: MouseEvent) => {
            const newX =event.pageX - shiftX;
            const newY = event.pageY - shiftY;

            // Get the viewport dimensions
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Get the card dimensions
            const cardWidth = cardDiv.offsetWidth;
            const cardHeight = cardDiv.offsetHeight;

            // Ensure the card stays within the viewport
            const boundedX = Math.max(0, Math.min(newX, viewportWidth - cardWidth));
            const boundedY = Math.max(0, Math.min(newY, viewportHeight - cardHeight));
            cardLocationRef.current = new DOMPoint(boundedX, boundedY);
            setLocation(cardLocationRef.current);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            //isDragging.current = false;
            setIsDragging(false);
            if (cardLocationRef.current !== null) {
                onCardDragEnd(id, cardLocationRef.current);
                cardLocationRef.current = null;
                setLocation(null);
            }
        };

        setIsDragging(true);
        const shiftX = e.clientX - cardDiv.getBoundingClientRect().left;
        const shiftY = e.clientY - cardDiv.getBoundingClientRect().top;

        // listeners.
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        e.preventDefault();
    };

    // if the card cannot be dragged, then ignore honorsOnly.
    const renderRank = (rank: string): JSX.Element | null => {
        if (canDrag) {
            if (gameSettings.honorsOnly && !isNaN(Number(rank))) {
                return null;
            }
            if (gameSettings.honorsOnly && !gameSettings.tenIsHonor && rank === 'T') {
                return null;
            }
        }
        if (rank === 'T' && !gameSettings.tenAsT) {
            return <>10</>;
        }
        return <>{rank}</>;
    };

    if (renderRank(rank) === null) {
        return null;
    } else {
        return (
            <div
                ref={cardRef}
                id={id}
                className={`suit-card ${revealed ? 'suit-card-border' : ''}`}
                style={{
                    ...(cardLocationRef.current && {
                        position: 'absolute',
                        left: `${cardLocationRef.current.x}px`,
                        top: `${cardLocationRef.current.y}px`,
                    }),
                    zIndex: isDragging ? 100 : 0,
                    cursor: canDrag ? 'pointer' : 'default',
                    color: makeGray ? 'darkgray' : 'black',
                }}
                onMouseDown={canDrag ? handleMouseDown : undefined}
            >
                {isDragging ? renderSuit(suit) : null}
                {renderRank(rank)}
            </div>
        );
    };
};

export default Card;